import CustomButton from "components/Atoms/CustomButton"
import CustomDateInput from "components/Atoms/CustomDateInput"
import StringInput from "components/Atoms/StringInput"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import VendorUserDetails from "components/Templates/form/VendorUserDetails"
import { EDUCATION_TYPES, GENDER_TYPES } from "data/static.items"
import {
  DC_SURVEY_AREAS,
  DISTRIBUTION_CENTERS,
  GROUPS,
  KYCS,
  ROUTES,
  ROUTE_CATEGORIES,
} from "graphql/selector.queries.gql"
import { ADD_UPDATE_VENDOR } from "graphql/vendor/vendor.mutations.gql"
import { VENDOR_DETAIL, VENDOR_LIST } from "graphql/vendor/vendor.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { cloneDeep, omit, pick, set } from "lodash-es"
import vendorModel from "models/vendor.model"
import React, { useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import {
  DocumentTypes,
  isInValidDocumentPresent,
  isValidDocument,
} from "utils/documentHelper"
import { remove } from "utils/ds.utils"
import OutletsMap from "./OutletsMap"
import { BlockUserNote } from "components/Common/BlockUserNote"

const VendorRegistration = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [state, setState] = useRouteForm(VENDOR_DETAIL, {
    default: {},
    transform(state) {
      return {
        ...state,
        gender: GENDER_TYPES.find(gt => gt.id === state.gender),
        education: EDUCATION_TYPES.find(et => et.id === state.education),
        routes: state.routepoints?.map(point => point.route),
      }
    },
  })

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    if (path === "routeCategory" || path === "surveyArea") {
      set(stateCopy, "routes", [])
    }
    if (path === "distributionCenter") {
      set(stateCopy, "surveyArea", null)
    }

    set(stateCopy, path, value)
    setState(stateCopy)
  }

  const parsedVariables = useMemo(() => {
    const kyc = state?.kycDetails?.[0]
    return {
      ...pick(state, [
        "id",
        "firstName",
        "lastName",
        "number",
        "email",
        "dateOfBirth",
        "username",
      ]),
      address: omit(state?.address, ["__typename"]),
      groups: state?.groups?.map(getId),
      education: state.education?.enum,
      gender: state.gender?.enum,
      kycDetail: {
        id: kyc?.id,
        idNumber: kyc?.idNumber,
        photo: kyc?.photo,
        kycType: state?.kycDetails?.[0]?.kycType?.id,
      },
      routeIds: state.routes?.map(route => route.id),
    }
  }, [state])

  const [errors] = useErrors({ schema: vendorModel, state: parsedVariables })
  const disabled = useFormDisabled("vendor", parsedVariables)
  return (
    <>
      <FormLayout id={id} type="Vendor">
        <FormController
          errors={errors?.details}
          source={state}
          onChange={handleChange}
          disabled={disabled  || state?.isBlocked}
          disableAll={true}
        >
          {state?.id && (
            <>
              <div className="flex items-baseline">
                <h5 className="mr-6 text-2xl">
                  <span className="text-muted">Vendor - </span> {state.fullName}{" "}
                  <span className="text-muted">
                    ( {state.customerId || "NA"} )
                  </span>
                </h5>
              </div>
              <hr className="mt-0 mb-3" />
              {state?.isBlocked ? (
                <BlockUserNote remark={state?.blockRemark} />
              ) : null}
            </>
          )}
          <VendorUserDetails disableAddress={state?.id} />
          <div className="grid grid-cols-12 gap-4 mt-3">
            <div className="col-span-6">
              <DynamicMultiSelector
                required
                path="groups"
                label="Vendor Types"
                fill
                big
                query={GROUPS}
                variables={{ category: "RTL" }}
              />
            </div>
            <div className="col-span-3">
              <CustomDateInput
                fill={true}
                label="Date Of Birth"
                path="dateOfBirth"
                helperText="eg. 27/06/1985"
              ></CustomDateInput>
            </div>
          </div>
          <h6 className="mt-6 vt-section-header">
            KYC Details{" "}
            <CustomButton
              icon="plus"
              intent="success"
              minimal
              onClick={e => {
                setState(prev => ({
                  ...prev,
                  kycDetails: prev?.kycDetails?.concat({}),
                }))
              }}
            />
          </h6>
          <hr className="mt-0" />
          {state?.kycDetails?.map((d, idx) => {
            const pan = state?.kycDetails?.find(doc => {
              return doc?.kycType?.name === DocumentTypes.PAN
            })?.idNumber
            return (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                  <DynamicSelector
                    big
                    query={KYCS}
                    path={`kycDetails.${idx}.kycType`}
                    label="Type"
                    helperText="eg. Aadhar"
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path={`kycDetails.${idx}.idNumber`}
                    label="Number"
                    helperText="eg. 435678986545"
                    error={
                      isValidDocument({
                        documentKind: d?.kycType?.name,
                        documentNumber: d?.idNumber,
                        pan: pan,
                        state: state?.address?.state,
                      }) || ""
                    }
                  />
                </div>
                <div className="col-span-3">
                  <FileUpload label="Photo" path={`kycDetails.${idx}.photo`} />
                </div>
                <div className="col-span-1 flex justify-center items-center">
                  <CustomButton
                    className="mb-4"
                    icon="trash"
                    intent="danger"
                    minimal
                    onClick={e => {
                      setState(prev => ({
                        ...prev,
                        kycDetails: remove(prev?.kycDetails, idx),
                      }))
                    }}
                  />
                </div>
              </div>
            )
          })}

          {process.env.NODE_ENV === "production" ? null : (
            <>
              <h6 className="mt-4 mb-0 vt-section-header">Assigned to route</h6>
              <p className="text-gray-600 text-sm mb-2">
                This vendor will get assigned to the selected route
              </p>
              <hr className="mt-0" />
              <div className="grid grid-cols-4 gap-4">
                <DynamicSelector
                  big
                  query={DISTRIBUTION_CENTERS}
                  path="distributionCenter"
                  label="Distribution Center"
                />
                <DynamicSelector
                  big
                  query={DC_SURVEY_AREAS}
                  variables={{
                    dcId: state?.distributionCenter?.id,
                  }}
                  name="properties.name"
                  searchVector={["properties.name"]}
                  path="surveyArea"
                  label="Survey Area"
                />
                <DynamicSelector
                  big
                  query={ROUTE_CATEGORIES}
                  path="routeCategory"
                  label="Route Category"
                />
                <DynamicMultiSelector
                  big
                  fill
                  query={ROUTES}
                  variables={{
                    filter: {
                      surveyAreaId: state?.surveyArea?.id,
                      categoryIds: [state?.routeCategory?.id],
                    },
                  }}
                  path="routes"
                  label="Routes"
                />
              </div>
            </>
          )}
        </FormController>
        {process.env.NODE_ENV === "production" ? null : (
          <OutletsMap
            items={state?.routes}
            vendorLocation={{
              lat: state?.address?.latitude,
              lng: state?.address?.longitude,
            }}
          />
        )}
        <MutationOptions
          className="mt-3"
          disabled={ state?.isBlocked}
          validate={() => {
            const docIsValid = isInValidDocumentPresent({
              documents: state?.kycDetails,
              state: state?.address?.state,
            })

            if (docIsValid !== undefined && !docIsValid) {
              ErrorToast("Please resolve all errors before proceeding")
              return false
            }

            if (errors.latest) {
              ErrorToast(errors?.latest)
              return false
            }
            return true
          }}
          refetchQueries={[
            {
              query: VENDOR_LIST,
              variables: {
                query: "",
                groupName: state?.groups?.[0]?.name,
                first: 13,
                skip: 0,
              },
            },
          ]}
          onSave={({ addUpdateVendor: auv }) => {
            const user = auv.user
            SuccessToast(`Vendor ${user.fullName} added successfully`)
            navigate(-1)
          }}
          onSaveAndAdd={({ addUpdateVendor: auv }) => {
            const user = auv.user
            SuccessToast(`Vendor ${user.fullName} added successfully`)
            setState({})
            navigate("/d/registration/vendors/details/new")
          }}
          onSaveAndEdit={({ addUpdateVendor: auv }) => {
            const user = auv.user
            SuccessToast(`Vendor ${user.fullName} added successfully`)
            if (!parseInt(id)) {
              navigate(`/d/registration/vendors/details/${user.id}`, {
                replace: true,
              })
            }
          }}
          mutation={ADD_UPDATE_VENDOR}
          variables={parsedVariables}
        />
      </FormLayout>
    </>
  )
}

export default VendorRegistration
